import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Button } from "reactstrap";
import classNames from "classnames";
import { findAll } from "highlight-words-core";

import EventText from "./EventText";
import EventActions from "./EventActions";
import HighlightedText from "./HighlightedText";
import TranscriptLegend from "./TranscriptLegend";
import TranscriptSearch from "./TranscriptSearch";
import { searchPhrasesFromText } from "./searchPhrasesFromText";

import MaterialIcon from "shared/react_components/MaterialIcon";
import Timestamp from "shared/react_components/Timestamp";

import {
  transcriptContainer, agent, caller, timestamp, icon, nonSpeakingEvent, speakingEvent, autoScrollButton,
  eventWithAutoScrollOff, eventActions, transcriptSearch
} from "./styles/transcript.module.scss";

const AUTO_SCROLL_PIXEL_BUFFER = 50;

const colors = { agent, caller };

const currentEventClass = (position, startTime, endTime) => {
  return (position >= startTime && position <= endTime) ? speakingEvent : nonSpeakingEvent;
};

const calculateSearchMatchTotal = ({ text, searchTextPhrases }) => {
  const chunks = findAll({ textToHighlight: text, searchWords: searchTextPhrases });
  return chunks.reduce((total, { highlight }) => (total += highlight ? 1 : 0), 0);
};

const cyclingIncrement = ({ current, increment, total }) => {
  const newValue = current + increment;
  if (newValue >= total) {
    return 0;
  } else if (newValue < 0) {
    return total - 1;
  } else {
    return newValue;
  }
};

const isItScrollable = function(el) {
  return el && (el.scrollHeight > el.offsetHeight);
};

const Transcript = ({ events, position, onAudioJumpPosition, onAddComment, enableAutoScroll, disableContainerScrollableReset }) => {
  const [containerScrollable, setContainerScrollable] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchScrollingEnabled, setSearchScrollingEnabled] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);

  const autoScrollEventRef = useRef();
  let autoScrollEventRefSet = false;

  const containerRef = useRef();

  const autoScrollRefParam = ({ startTime, endTime }) => {
    if (!autoScrollEventRefSet && typeof position === "number" && startTime <= position && endTime >= position) {
      autoScrollEventRefSet = true;
      return { ref: autoScrollEventRef };
    } else {
      return {};
    }
  };

  useEffect(() => {
    // Don't check if the container is scrollable, instead just try to scroll anyway. This is necessary
    // because it inherently causes `containerScrollable` to be set to true, which is then used by `onWheel`.
    if (enableAutoScroll && autoScroll && autoScrollEventRef.current) {
      containerRef.current.scrollTop = autoScrollEventRef.current.offsetTop - containerRef.current.offsetTop - AUTO_SCROLL_PIXEL_BUFFER;
    }

    disableContainerScrollableReset || setContainerScrollable(isItScrollable(containerRef.current));
  });

  // If it's not possible for the container to be scrolled, onScroll won't be called.
  // This can happen via the user or via the `scrollIntoView` above.
  const onScroll = () => { containerScrollable || setContainerScrollable(true); };

  // Only triggered by user's mouse/trackpad wheel scroll.
  const onWheel = () => { containerScrollable && setAutoScroll(false); };

  const searchTextPhrases = useMemo(() => searchPhrasesFromText({ searchText }), [searchText]);
  const searchMatchTotal = useMemo(() => {
    return events.reduce((total, { text }) => total + calculateSearchMatchTotal({ text, searchTextPhrases }), 0);
  }, [events, searchText]);

  const activeSearchIndexIncrement = value => {
    if (searchMatchTotal > 0) {
      const newActiveSearchIndex = cyclingIncrement({ current: activeSearchIndex, increment: value, total: searchMatchTotal });
      setActiveSearchIndex(newActiveSearchIndex);
      setAutoScroll(false);
    }
  };

  const changeSearchText = value => {
    setActiveSearchIndex(-1);
    setSearchText(value);
  };

  const searchFocusChange = value => setSearchScrollingEnabled(value);

  let activeMatchIndex = activeSearchIndex;
  let activeMatchFound = false;

  return (
    <>
      <TranscriptSearch
        className={transcriptSearch}
        searchText={searchText}
        onSearchTextChange={changeSearchText}
        onActiveSearchIndexIncrement={activeSearchIndexIncrement}
        activeIndex={activeSearchIndex}
        searchMatchTotal={searchMatchTotal}
        onSearchFocusChange={searchFocusChange}
      />

      <div className={transcriptContainer} ref={containerRef} onScroll={onScroll} onWheel={onWheel}
        data-test-id="transcript-container"
      >
        {events.map(({ startTime, endTime, speaker, text, matchedPhrases }, index) => {
          const matchCount = calculateSearchMatchTotal({ text, searchTextPhrases });

          let activeIndexParam = {};
          if (searchScrollingEnabled) {
            if (searchTextPhrases.length > 0 && !activeMatchFound) {
              if (matchCount > activeMatchIndex) {
                activeIndexParam = { activeIndex: activeMatchIndex };
                activeMatchFound = true;
              } else {
                activeMatchIndex -= matchCount;
              }
            }
          }

          return (
            <div className={classNames(currentEventClass(position, startTime, endTime), autoScroll ? "" : eventWithAutoScrollOff)}
              key={startTime} {...autoScrollRefParam({ startTime, endTime })} data-test-id="transcript-event-container"
            >
              <span className={icon}>
                <MaterialIcon name="person" className={colors[speaker]}/>
                <Timestamp className={timestamp} seconds={startTime} trimLeadingZeros/>
              </span>

              {searchTextPhrases.length > 0
                ? <HighlightedText text={text} searchTextPhrases={searchTextPhrases} {...activeIndexParam} />
                : <EventText key={`event-text-${index}`} text={text} matchedPhrases={matchedPhrases} />
              }

              {(onAudioJumpPosition || onAddComment) &&
                <div className={eventActions} data-test-id="event-actions-container">
                  <EventActions
                    onAudioJumpPosition={onAudioJumpPosition}
                    startTime={startTime}
                    onAddComment={onAddComment && (() => onAddComment({ startTime, speaker, text }))}
                  />
                </div>
              }
            </div>
          );
        })}

        {enableAutoScroll && !autoScroll && containerScrollable &&
          <Button className={autoScrollButton} onClick={() => setAutoScroll(true)} data-test-id="auto-scroll">
            Resume auto-scroll
          </Button>
        }
      </div>
    </>
  );
};

Transcript.propTypes = {
  /**
   * The transcript data.
   * This is a time ordered array of hashes with members :start_time, :speaker, and :text.
   * The timestamps are floats in seconds relative to the start of the call.
   * The possible :speaker values :agent and :caller.
   * The :text is a string.
   */
  events: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.number,
      endTime: PropTypes.number,
      speaker: PropTypes.string,
      text: PropTypes.string,
      matchedPhrases: PropTypes.arrayOf(
        PropTypes.shape({
          textStartOffset: PropTypes.number,
          textEndOffset: PropTypes.number,
          searchPhrase: PropTypes.string
        })
      )
    })
  ),
  /**
   * The current time corresponding to the AudioPlayer
   */
  position: PropTypes.number,
  /**
   * Function that accepts a "position" argument. When a function is given that means audio
   * jumping via the rows' play buttons is supported.
   */
  onAudioJumpPosition: PropTypes.func,
  /**
   * Function that accepts an object with event details: ({ startTime, speaker, text }).
   * When a function is given that means commenting is enabled, and users can reference specific
   * transcript events in comments.
   */
  onAddComment: PropTypes.func,
  /**
   * Whether or not auto scrolling is enabled
   */
  enableAutoScroll: PropTypes.bool,
  /**
   * Prop only used for tests
   */
  disableContainerScrollableReset: PropTypes.bool
};

Transcript.defaultProps = {
  enableAutoScroll: false,
  disableContainerScrollableReset: false
};

export { TranscriptLegend };

export default Transcript;
